/**
 * THE FROZEN ROWS A RUN USED (`SPEC-APP.md` §11.6). Props in, NO query of its own.
 *
 * A PLAIN TABLE in the shape of group 2 Phase 14's `IssuesTable` — not `RegisterGrid`: the wire
 * sends every row at once in one unpaged object, so the page control below is in memory over the
 * array, never a server window.
 *
 * EVERY QUANTITY IS A DECIMAL STRING and every side field is nullable: a null side renders "—",
 * never a zero and never a computed value. The run-scoped payload sends one decimal (`"0.0"`)
 * where the live workspace sends three (`"0.000"`); `formatQuantity` renders both and neither is
 * parsed.
 *
 * A SIDE'S `source` CARRIES A RAW TOKEN (`webapp_edit`, `kc-integration-api`) AND NEVER RENDERS AS
 * COPY (`PROJECT-KNOWLEDGE.md` §Terminology law, §Formatting & copy). A side whose override id is
 * set reads "planner override"; every other reads as the SAP delivery.
 */
import { useMemo, useState } from "react";
import { PencilLine } from "lucide-react";

import { useReferenceLookup } from "../../../../api/queries/useReferenceLookup";
import { CodeTag } from "../../../../components/common/CodeTag";
import { plainTable } from "../../../../components/grid/gridTheme";
import { Pager } from "../../../../components/grid/Pager";
import { Skeleton } from "../../../../components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../../../components/ui/table";
import { formatDate } from "../../../../domain/format";
import { formatQuantity } from "../../../../domain/units";
import type { RunSalesOutboundRow } from "../../hooks/useRunSalesOutbound";
import { OutcomeChip } from "./OutcomeChip";

/** In-memory window. Today the wire sends 629 rows in one object. */
export const RUN_OUTBOUND_PAGE_SIZE = 50;

export interface RunSalesOutboundTableProps {
  rows: RunSalesOutboundRow[];
  /**
   * The dictionary noun for a site. It arrives as a PROP, not from `useLabels`: this table hangs
   * off the module descriptor, and reading the label hook here would close an import cycle
   * (`module.ts` → slot → `useLabels` → the tenant registry → `module.ts`). KCC's own dictionary
   * word is the default.
   */
  siteLabel?: string;
}

/** The two words this module allows for a side's provenance. The raw token never renders. */
export function provenanceLabel(overrideId: string | null | undefined): string {
  return overrideId ? "planner override" : "SAP delivery";
}

function SideCell({
  quantity,
  overrideId,
}: {
  quantity?: string | null;
  overrideId?: string | null;
}) {
  if (quantity === null || quantity === undefined) {
    return <span className="type-table text-muted-foreground">—</span>;
  }
  const overridden = Boolean(overrideId);
  return (
    <span className="type-table inline-flex items-center justify-end gap-1">
      {formatQuantity(quantity)}
      {overridden ? (
        <PencilLine aria-hidden className="size-3 shrink-0 text-muted-foreground" />
      ) : null}
      <span className="type-caption text-muted-foreground">{provenanceLabel(overrideId)}</span>
    </span>
  );
}

export function RunSalesOutboundTable({
  rows,
  siteLabel = "Location",
}: RunSalesOutboundTableProps) {
  const lookup = useReferenceLookup();
  const [page, setPage] = useState(0);

  const window = useMemo(() => {
    const start = page * RUN_OUTBOUND_PAGE_SIZE;
    return rows.slice(start, start + RUN_OUTBOUND_PAGE_SIZE);
  }, [rows, page]);

  return (
    <div className="flex flex-col gap-xs">
      <div className="flex justify-end">
        <Pager page={page} pageSize={RUN_OUTBOUND_PAGE_SIZE} total={rows.length} onPage={setPage} />
      </div>

      <Table className={plainTable.root}>
        <TableHeader>
          <TableRow className={plainTable.row}>
            <TableHead className={plainTable.head}>{siteLabel}</TableHead>
            <TableHead className={plainTable.head}>Sales day</TableHead>
            <TableHead className={plainTable.head}>Sales orders</TableHead>
            <TableHead className={plainTable.head}>Sales forecast</TableHead>
            <TableHead className={plainTable.head}>Sales volume used in plan</TableHead>
            <TableHead className={plainTable.head}>Sides used</TableHead>
            <TableHead className={plainTable.head}>Rule version</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {window.map((row) => {
            const site = lookup.site(row.siteId);
            return (
              <TableRow key={`${row.siteId}:${row.salesDate}`} className={plainTable.row}>
                <TableCell className={plainTable.cell}>
                  {site ? (
                    <span className="inline-flex items-center gap-2xs">
                      <CodeTag value={site.siteCode} />
                      <span className="text-muted-foreground">{site.name}</span>
                    </span>
                  ) : (
                    <Skeleton className="h-3 w-24" />
                  )}
                </TableCell>
                <TableCell className={`${plainTable.cell} ${plainTable.numeric}`}>
                  {formatDate(row.salesDate)}
                </TableCell>
                <TableCell className={`${plainTable.cell} ${plainTable.numeric}`}>
                  <SideCell quantity={row.orderQuantity} overrideId={row.orderOverrideId} />
                </TableCell>
                <TableCell className={`${plainTable.cell} ${plainTable.numeric}`}>
                  <SideCell quantity={row.forecastQuantity} overrideId={row.forecastOverrideId} />
                </TableCell>
                <TableCell className={`${plainTable.cell} ${plainTable.numeric}`}>
                  {formatQuantity(row.quantity)}
                </TableCell>
                <TableCell className={plainTable.cell}>
                  <OutcomeChip
                    value={(row.outcome as Parameters<typeof OutcomeChip>[0]["value"]) ?? null}
                  />
                </TableCell>
                <TableCell className={plainTable.cell}>
                  {row.ruleVersion ? (
                    <CodeTag value={row.ruleVersion} />
                  ) : (
                    <span className="text-muted-foreground">—</span>
                  )}
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
    </div>
  );
}
